<h4>What this screen is for</h4>
<p>
    Operational settings that change how the overnight run behaves, one tab per set. All
    of them are <strong>shared across your whole operator</strong>, not personal
    preferences &mdash; saving replaces the setting for everyone.
</p>

<h4>Yield Protection</h4>
<p>
    Yield protection stops the recommendation from re-opening cheaper buckets once a
    journey has moved up the price stack. With it on, anything below the journey's
    lead bucket is closed and its allocation rolled into the lead bucket.
</p>
<ul>
    <li><strong>Enable</strong> &mdash; the master switch. The other three settings are ignored while it is off.</li>
    <li>
        <strong>Activation Days Prior</strong> &mdash; protection only applies inside this
        many days of departure. Journeys further out are left alone. Leave it unset and
        protection applies at every days-prior.
    </li>
    <li>
        <strong>Services/Markets</strong> &mdash; which services this applies to. You can
        name individual services or a whole market. Leave it empty to cover everything.
    </li>
    <li>
        <strong>Inclusive / Exclusive</strong> &mdash; the switch beside the list.
        <em>Inclusive</em> protects only the services listed. <em>Exclusive</em> protects
        everything <em>except</em> them.
    </li>
</ul>

<h4>Optimization Schedule Override</h4>
<p>
    Normally the overnight run optimises a standard set of days-prior. This lets you
    replace that set for one night &mdash; useful when you want to concentrate the
    run on particular points in the booking curve.
</p>
<p>
    The dropdown shows calendar dates, but what you are choosing is a
    <strong>number of days before departure</strong>. The dates are simply that
    offset counted from today, so the same saved selection will read as different
    dates tomorrow. The heading tells you how many days will be optimised tonight.
</p>

<h4>Protected Buckets</h4>
<p>
    Protected buckets are seats held back outside revenue management. Switching one on
    here means the part of its hold that has <em>not</em> sold is taken off the capacity
    the optimiser believes it has left to sell, so it stops planning to sell seats that
    are not for sale.
</p>
<p>
    <strong>Treat this as a dangerous setting.</strong> The hold is recorded per
    origin&ndash;destination pair, and the reduction is <em>summed across every OD that
    travels over a leg</em>. A bucket whose limit is set to the same value network-wide is
    therefore multiplied by the number of ODs on the leg rather than applied once. In
    testing, four buckets each holding a blanket 10, 10, 10 and 5 on nearly every OD took
    roughly 640 seats off a 124-seat leg, and 83% of legs ended up with nothing left to
    sell. Only switch on a bucket whose limits are set deliberately for specific ODs, and
    check those limits first.
</p>
<ul>
    <li>
        Only <strong>protected, non-flex</strong> buckets are listed. A flex bucket is
        excluded because its allocation is the entire class capacity, so subtracting it
        would always floor the leg at zero.
    </li>
    <li>
        <strong>Bookings already made</strong> in a protected bucket reduce capacity
        whether or not it is switched on here. This setting is only about the unsold
        remainder of the hold.
    </li>
    <li>
        The list comes from the current bucket table, which has no validity dates,
        whereas the run reads a date-versioned bucket file. So a bucket can appear here
        and still be ignored for a particular departure date.
    </li>
    <li>
        <strong>Until this tab has been saved once</strong> the switches show the saved
        value &mdash; all off &mdash; which is not necessarily what the run is currently
        using. A note on the tab tells you when that is the case.
    </li>
</ul>

<h4>OD Groups</h4>
<p>
    An OD group ties one or more child origin&ndash;destination pairs to a parent OD.
    <strong>The child then inherits the parent's booking limits</strong>, and limits
    reach the reservation system under the parent rather than under the child.
</p>
<p>
    Pick a parent OD and a child OD in the form &mdash; the destination list narrows to
    what the chosen origin actually serves &mdash; then <strong>ADD GROUP</strong>.
    Existing groups are listed underneath with EDIT and REMOVE, and the filter box
    matches on either side of the pair. Nothing is written until you Save.
</p>
<ul>
    <li>
        <strong>A child OD can belong to only one parent, and groups are one level
        deep</strong> &mdash; an OD cannot be a parent and a child. Duplicate pairs and an
        OD listed as its own child are rejected too. Problems are listed on the tab as you
        introduce them.
    </li>
    <li>
        <strong>An invalid list blocks the whole Save, not just this tab</strong>, so that
        the other tabs cannot be saved while the groups are not. Fix what is listed and
        save again.
    </li>
    <li>
        Where OD grouping is not switched on for your operation, groups can still be set
        up here but nothing will apply them. The tab says so.
    </li>
    <li>
        Grouping also shows up when you override: a child OD cannot be sent to the
        reservation system on its own, and the Inventory override window explains that
        rather than offering it.
    </li>
</ul>

<h4>System Settings</h4>
<p>
    A read-only view of this tenant's installed settings file, as the overnight run
    reads it, grouped by area and searchable by setting or group name. Nothing here is
    editable and nothing on this tab is affected by Save.
</p>
<ul>
    <li>Values that look credential-bearing are masked by the API and never reach the browser.</li>
    <li>A nested value shows its type and size; expand the row to see the whole thing.</li>
    <li>The tab is only shown to administrators, and the API refuses the request for anyone else.</li>
</ul>

<h4>Saving</h4>
<p>
    <strong>Save</strong> writes every tab together, not just the one you are looking at,
    and confirms with a brief message. There is no confirmation prompt. OD groups are
    written by a second request, so it is possible for one half to fail; the message
    reports what happened.
</p>

<h4>Worth knowing</h4>
<ul>
    <li>
        <strong>Nothing here changes anything you can see straight away.</strong> These
        settings are read by the overnight run, so the effect appears in tomorrow's
        recommendations.
    </li>
    <li>
        <strong>The Optimization Schedule Override is cleared once it has been used.</strong>
        After the overnight run the screen goes back to showing the standard schedule.
        That is by design, not a lost save.
    </li>
    <li>
        Turning yield protection on takes a full overnight cycle to bite, because the
        lead buckets it protects are captured during that run.
    </li>
    <li>
        <strong>SELECT ALL</strong> on Services/Markets selects the entire list, not just
        what your search box has filtered to.
    </li>
    <li>
        Yield protection can quietly do nothing if no journeys fall inside the
        days-prior window or match the service list. There is no warning on screen.
    </li>
    <li>
        If the protected bucket list or the OD groups cannot be loaded, the tab says so
        and saving leaves that setting untouched rather than wiping it.
    </li>
</ul>
